import { createApi } from "@reduxjs/toolkit/query/react";
import { useDispatch } from "react-redux";
import {
    LoanLutQuery,
    LoanPnlRequest,
    LoanPnlStats,
    ROUTE_LOAN_LUT,
    StrategyDuration,
    TXN_LEND_VAULT_MINT_LP_IXS,
    VaultMintLpParams
} from "@bridgesplit/abf-sdk";
import { SwapInstructionsResponse } from "@loopscale/pandora-types";

import {
    MARKET_DURATIONS,
    MARKET_QUOTE_FOR_APY_ROUTE,
    MARKET_QUOTE_ROUTE,
    ROUTE_REFRESH_MARKET_TVL_STATS,
    GET_MIN_PRINCIPAL_ROUTE,
    ROUTE_FETCH_MARKET_INFORMATIONS,
    GET_MARKET_STATS_ROUTE,
    GET_LOAN_BY_ADDRESS_ROUTE,
    GET_LOANS_WITH_INFO_ROUTE,
    GET_HISTORICAL_LOANS_ROUTE,
    ROUTE_INCREASE_CREDIT_QUOTE,
    ROUTE_LOAN_SALE_QUOTE,
    ROUTE_LOAN_SALE_QUOTE_BATCH,
    ROUTE_MERGE_PLAN_QUOTE,
    GET_BEST_LOOP_QUOTE_ROUTE,
    GET_BEST_QUOTE_ROUTE,
    GET_MAX_QUOTE_ROUTE,
    GET_STRATEGY_INFO_ROUTE,
    GET_STRATEGY_SUMMARIES_ROUTE,
    GET_RATE_HISTORY_ROUTE,
    ROUTE_GET_PRINCIPAL_COLLATERAL_RATE_HISTORY,
    ROUTE_LOANS_PNL,
    ROUTE_LOANS_PNL_HISTORY,
    ROUTE_FETCH_PERMISSIONED_LOANS
} from "../constants";
import { abfSerializeQueryArgs, marketsPublicBaseQuery } from "./util";
import {
    BestLoopQuote,
    BestLoopQuoteQuery,
    BestQuote,
    BestQuoteFilter,
    CollateralQuoteFilter,
    CollateralQuoteFilterForApy,
    HistoricalMarketRate,
    PrincipalCollateralRateHistoryResponse,
    IncreasePrincipalQuote,
    LendingStrategyFilter,
    LoanFilterApi,
    LoanInfo,
    LoanInfoResponse,
    MarketInformationsFilter,
    MarketStratResponse,
    MaxQuote,
    MaxQuoteRequest,
    OrderbookQuote,
    OrderbookQuoteForApy,
    ParsedMarketInformationWithLtvInfo,
    PermissionedLoan,
    PermissionedLoanFilter,
    PrincipalMarketStrategyStats,
    RateMarketType,
    RatesHistoryParams,
    RawMarketStratResponse,
    RawMaxQuote,
    RawPrincipalMarketStrategyStats,
    RawUnifiedStatsResponse,
    MultiSellLoanQuoteEntry,
    MultiSellLoanQuoteRequest,
    SellLoanQuote,
    UnifiedStatsRequest,
    UnifiedStatsResponse,
    PrincipalCollateralBorrowRateQuery
} from "../types";
import { deserializeStrategyDurationOrUndefined } from "../utils";
import { parseMarketInformationWithLtvInfoFromApi, parseStrategyInfoResponseFromApi } from "../types/parsers/";
import { ApiStrategyInfoResponse } from "../types/api/strategy";
import { StrategyInfoResponse } from "../types/domain/strategy";
import { parseApiLoanDetail, parseApiLoanSummary } from "../types/parsers/loan-parser";
import {
    ApiHistoricalLoansRequest,
    ApiHistoricalLoansResponse,
    ApiLoanDetail,
    ApiLoanSummary,
    MergePlanQuoteRequest,
    MergePlannerRankedResponse,
    ParsedMarketInformationWithLtvInfoApi
} from "../types/api";

const MARKET_INFO_TAG = "MarketInformation";
const MARKETS_PUBLIC_TAG = "MarketsPublic";
const LOANS_TAG = "Loans";
const LOAN_LUTS_TAG = "LoanLuts";
const LENDING_STRATEGY_TAG = "LendingStrategy";

export const marketsPublicApi = createApi({
    reducerPath: "marketsPublicApi",
    baseQuery: marketsPublicBaseQuery,
    serializeQueryArgs: abfSerializeQueryArgs,

    tagTypes: [MARKETS_PUBLIC_TAG, LOANS_TAG, MARKET_INFO_TAG, LENDING_STRATEGY_TAG, LOAN_LUTS_TAG],
    endpoints: (builder) => ({
        strategyDurations: builder.query<StrategyDuration[], void>({
            // market durations are hardcoded for now to reduce latency
            queryFn() {
                return {
                    data: MARKET_DURATIONS
                };
            }
        }),

        //Loan info route
        loanInfos: builder.query<LoanInfoResponse, LoanFilterApi>({
            providesTags: [LOANS_TAG],
            query(body) {
                return {
                    url: GET_LOANS_WITH_INFO_ROUTE,
                    method: "POST",
                    body
                };
            },
            transformResponse: (response: ApiLoanSummary): LoanInfoResponse => {
                return parseApiLoanSummary(response);
            }
        }),

        historicalLoans: builder.query<ApiHistoricalLoansResponse, ApiHistoricalLoansRequest>({
            providesTags: [LOANS_TAG],
            query(body) {
                return {
                    url: GET_HISTORICAL_LOANS_ROUTE,
                    method: "POST",
                    body
                };
            }
        }),

        loanDetail: builder.query<LoanInfo, { address: string; includePnl?: boolean }>({
            providesTags: [LOANS_TAG],
            query({ address, includePnl }) {
                // `?includePnl=true` bundles the per-loan PnL breakdown inline so the caller
                // doesn't have to fall back to a follow-up `/loans/pnl` fetch.
                const qs = includePnl ? "?includePnl=true" : "";
                return {
                    url: `${GET_LOAN_BY_ADDRESS_ROUTE}/${address}${qs}`,
                    method: "GET"
                };
            },
            transformResponse: (response: ApiLoanDetail): LoanInfo => {
                return parseApiLoanDetail(response);
            }
        }),

        // Market stats route
        stats: builder.query<UnifiedStatsResponse, UnifiedStatsRequest>({
            providesTags: [MARKETS_PUBLIC_TAG],
            query(body) {
                return {
                    url: GET_MARKET_STATS_ROUTE,
                    method: "POST",
                    body
                };
            },
            transformResponse: (response: RawUnifiedStatsResponse) => ({
                strategy: response.strategy ? convertToMarketStratResponse(response.strategy) : undefined,
                principal: response.principal,
                collateral: response.collateral,
                rates: response.rates,
                principalToDepositedCollateralUsd: response.principalToDepositedCollateralUsd,
                borrowCaps: response.borrowCaps,
                ecosystem: response.ecosystem,
                earn: response.earn,
                totals: response.totals
            })
        }),
        refreshMarketTvl: builder.query<void, void>({
            query() {
                return {
                    url: ROUTE_REFRESH_MARKET_TVL_STATS,
                    method: "POST"
                };
            }
        }),

        //Market quotes routes
        marketQuotes: builder.query<OrderbookQuote[], CollateralQuoteFilter>({
            providesTags: [MARKETS_PUBLIC_TAG],
            query(body) {
                return {
                    url: MARKET_QUOTE_ROUTE,
                    method: "POST",
                    body
                };
            }
        }),
        marketQuotesForApy: builder.query<OrderbookQuoteForApy[], CollateralQuoteFilterForApy>({
            providesTags: [MARKETS_PUBLIC_TAG],
            query(body) {
                return {
                    url: MARKET_QUOTE_FOR_APY_ROUTE,
                    method: "POST",
                    body
                };
            }
        }),
        increasePrincipalQuote: builder.query<IncreasePrincipalQuote, string>({
            providesTags: [MARKETS_PUBLIC_TAG],
            query(loanAddress: string) {
                return {
                    url: `${ROUTE_INCREASE_CREDIT_QUOTE}/${loanAddress}`,
                    method: "GET"
                };
            }
        }),
        sellQuote: builder.query<SellLoanQuote, string>({
            providesTags: [MARKETS_PUBLIC_TAG],
            query(loanAddress: string) {
                return {
                    url: `${ROUTE_LOAN_SALE_QUOTE}/${loanAddress}`,
                    method: "GET"
                };
            }
        }),
        sellQuoteBatch: builder.query<MultiSellLoanQuoteEntry[], MultiSellLoanQuoteRequest>({
            providesTags: [MARKETS_PUBLIC_TAG],
            query(body) {
                return {
                    url: ROUTE_LOAN_SALE_QUOTE_BATCH,
                    method: "POST",
                    body
                };
            }
        }),
        mergePlanQuote: builder.query<MergePlannerRankedResponse, MergePlanQuoteRequest>({
            providesTags: [MARKETS_PUBLIC_TAG],
            query(body) {
                return {
                    url: ROUTE_MERGE_PLAN_QUOTE,
                    method: "POST",
                    body
                };
            }
        }),

        // Strategy info routes
        lendingStrategyInfos: builder.query<StrategyInfoResponse, LendingStrategyFilter>({
            providesTags: [LENDING_STRATEGY_TAG],
            // Re-expanding a previously-opened group should be instant; bump from default 60s.
            keepUnusedDataFor: 300,
            query(filter) {
                return {
                    url: GET_STRATEGY_INFO_ROUTE,
                    method: "POST",
                    body: { ...filter }
                };
            },
            transformResponse: (response: ApiStrategyInfoResponse) => {
                return parseStrategyInfoResponseFromApi(response);
            }
        }),
        // Summary variant: same shape as lendingStrategyInfos but terms.assetTerms is
        // empty unless `collateralTermsAssetFilter` is passed — the mia/scl joins
        // are skipped server-side to avoid the per-strategy row fan-out. Use for
        // contexts that don't need full collateral terms.
        lendingStrategySummaries: builder.query<StrategyInfoResponse, LendingStrategyFilter>({
            providesTags: [LENDING_STRATEGY_TAG],
            query(filter) {
                return {
                    url: GET_STRATEGY_SUMMARIES_ROUTE,
                    method: "POST",
                    body: { ...filter }
                };
            },
            transformResponse: (response: ApiStrategyInfoResponse) => {
                return parseStrategyInfoResponseFromApi(response);
            }
        }),
        // Returns a map of principal mint to minimum amount.
        presetPrincipalByMints: builder.query<Record<string, number>, string[]>({
            providesTags: [MARKETS_PUBLIC_TAG],
            query(body) {
                return {
                    url: GET_MIN_PRINCIPAL_ROUTE,
                    method: "POST",
                    body
                };
            }
        }),

        // markert rates history routes
        ratesHistory: builder.query<Record<RateMarketType, HistoricalMarketRate[]>, RatesHistoryParams>({
            providesTags: [MARKETS_PUBLIC_TAG],
            query(body) {
                return {
                    url: GET_RATE_HISTORY_ROUTE,
                    method: "POST",
                    body
                };
            }
        }),
        principalCollateralRateHistory: builder.query<
            PrincipalCollateralRateHistoryResponse,
            PrincipalCollateralBorrowRateQuery
        >({
            providesTags: [MARKETS_PUBLIC_TAG],
            query(body) {
                return {
                    url: ROUTE_GET_PRINCIPAL_COLLATERAL_RATE_HISTORY,
                    method: "POST",
                    body
                };
            }
        }),
        // Oracle market informations route
        oracleMarketInformations: builder.query<ParsedMarketInformationWithLtvInfo[], MarketInformationsFilter>({
            providesTags: [MARKET_INFO_TAG],
            query(params) {
                return {
                    url: ROUTE_FETCH_MARKET_INFORMATIONS,
                    body: params,
                    method: "POST"
                };
            },
            transformResponse: (response: ParsedMarketInformationWithLtvInfoApi[]) => {
                return response.map(parseMarketInformationWithLtvInfoFromApi);
            }
        }),

        //Quotes
        loopBestQuote: builder.query<BestLoopQuote | null, BestLoopQuoteQuery>({
            providesTags: [MARKETS_PUBLIC_TAG],
            query(body) {
                return {
                    url: GET_BEST_LOOP_QUOTE_ROUTE,
                    method: "POST",
                    body
                };
            }
        }),
        maxQuotes: builder.query<MaxQuote[], MaxQuoteRequest>({
            providesTags: [MARKETS_PUBLIC_TAG],
            query(body) {
                return {
                    url: GET_MAX_QUOTE_ROUTE,
                    method: "POST",
                    body
                };
            },
            transformResponse: convertRawQuote
        }),
        bestQuotes: builder.query<BestQuote[], BestQuoteFilter>({
            providesTags: [MARKETS_PUBLIC_TAG],
            query(body) {
                return {
                    url: GET_BEST_QUOTE_ROUTE,
                    method: "POST",
                    body
                };
            },
            transformResponse: convertRawBestQuotes
        }),

        getLoanLut: builder.query<string | null, LoanLutQuery>({
            providesTags: [LOAN_LUTS_TAG],
            query: ({ loan, actionType }) => ({
                url: `${ROUTE_LOAN_LUT}?loan=${loan}&actionType=${actionType}`,
                method: "GET"
            })
        }),

        loanPnl: builder.query<Record<string, LoanPnlStats>, LoanPnlRequest>({
            providesTags: [LOANS_TAG],
            query(body) {
                return {
                    url: ROUTE_LOANS_PNL,
                    method: "POST",
                    body
                };
            }
        }),

        loanPnlGraph: builder.query<Record<string, LoanPnlStats>, LoanPnlRequest>({
            providesTags: [LOANS_TAG],
            query(body) {
                // Graph query uses /loans/pnl/history endpoint
                // Current PnL is queried separately and inserted at time=now in the frontend
                return {
                    url: ROUTE_LOANS_PNL_HISTORY,
                    method: "POST",
                    body
                };
            }
        }),

        // Permissioned loans route
        fetchPermissionedLoans: builder.query<PermissionedLoan[], PermissionedLoanFilter>({
            providesTags: [LOANS_TAG],
            query(body) {
                return {
                    url: ROUTE_FETCH_PERMISSIONED_LOANS,
                    method: "POST",
                    body
                };
            }
        }),
        marketsWindQuoteIxs: builder.query<SwapInstructionsResponse, VaultMintLpParams>({
            providesTags: [MARKETS_PUBLIC_TAG],
            query: (body) => ({
                url: TXN_LEND_VAULT_MINT_LP_IXS,
                method: "POST",
                body
            })
        })
    })
});

export const {
    useStrategyDurationsQuery,
    useStatsQuery,
    useMarketQuotesQuery,
    useMarketQuotesForApyQuery,
    usePresetPrincipalByMintsQuery,
    useRatesHistoryQuery,
    usePrincipalCollateralRateHistoryQuery,
    useOracleMarketInformationsQuery,
    useLoanInfosQuery,
    useHistoricalLoansQuery,
    useLoanDetailQuery,
    useIncreasePrincipalQuoteQuery,
    useSellQuoteQuery,
    useSellQuoteBatchQuery,
    useMergePlanQuoteQuery,
    useLendingStrategyInfosQuery,
    useLendingStrategySummariesQuery,
    useLoopBestQuoteQuery,
    useMaxQuotesQuery,
    useBestQuotesQuery,
    useGetLoanLutQuery,
    useLoanPnlQuery,
    useLoanPnlGraphQuery,
    useFetchPermissionedLoansQuery,
    useMarketsWindQuoteIxsQuery
} = marketsPublicApi;

export const useMarketsPublicApi = () => {
    const dispatch = useDispatch();
    const [fetchMarketStats] = marketsPublicApi.endpoints.stats.useLazyQuery();

    return {
        fetchMarketStats,
        resetMarketsPublicApi: () => dispatch(marketsPublicApi.util.invalidateTags([MARKETS_PUBLIC_TAG])),
        resetMarketInfoApi: () => dispatch(marketsPublicApi.util.invalidateTags([MARKET_INFO_TAG])),
        resetLendingStrategyApi: () => dispatch(marketsPublicApi.util.invalidateTags([LENDING_STRATEGY_TAG])),
        resetLoanApi: () => dispatch(marketsPublicApi.util.invalidateTags([LOANS_TAG])),
        resetLoanLuts: () => dispatch(marketsPublicApi.util.invalidateTags([LOAN_LUTS_TAG]))
    };
};

function convertToMarketStratResponse(input: RawMarketStratResponse): MarketStratResponse {
    const result: MarketStratResponse = {};

    for (const [mint, data] of Object.entries(input)) {
        result[mint] = transformPrincipalMarketStats(data);
    }

    return result;
}

export function transformPrincipalMarketStats(raw: RawPrincipalMarketStrategyStats): PrincipalMarketStrategyStats {
    return {
        totalDeposits: raw.totalDeposits,
        durationToMinApy: Object.entries(raw.durationToMinApy).map(([key, apy]) => {
            const strategyDuration = deserializeStrategyDurationOrUndefined(key);
            if (!strategyDuration) {
                throw new Error(`Invalid strategy duration: ${key}`);
            }
            return {
                duration: strategyDuration.duration,
                durationType: strategyDuration.durationType,
                apy: apy
            };
        })
    };
}

function convertRawQuote(raw: RawMaxQuote[]) {
    return raw.map((quote) => {
        return {
            apy: quote.apy,
            lendingStrategyAddress: quote.strategy,
            ltv: quote.ltv,
            liquidationThreshold: quote.lqt,
            principalAvailable: quote.amount,
            collateralMint: quote.collateralIdentifier
        };
    });
}

function convertRawBestQuotes(raw: Record<string, Omit<BestQuote, "collateralMint">[]>) {
    return Object.entries(raw)
        .map(([collateralMint, quotes]) => quotes.map((bestQuote): BestQuote => ({ ...bestQuote, collateralMint })))
        .flat();
}
